import { type ValueEvent } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { permissionGuard } from '@adornis/user-forms/permission-guard.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import { CampusScope } from '@chancenwerk/db/Scopes.js';
import { ChancenPermission } from '@chancenwerk/db/permissions.js';
import { Role } from '@chancenwerk/db/semantics/Role.js';
import { type ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { CampusDialogEditScope } from '../components/dialogs/campus-dialog-edit-scope.js';
import './campus-page-no-permission.js';

/**
 * @customElement campus-page-administration-scopes
 */
@customElement('campus-page-administration-scopes')
export class CampusPageAdministrationScopes extends ChemistryLitElement {
  @state() private readonly _currentUser = new RXController(this, currentUser<ChancenwerkUser>());

  @state() private readonly _scopes = new RXController(
    this,
    CampusScope.subscribeAll<CampusScope>()(CampusScope.allFields),
  );

  @state() private readonly _userSearchText = new RXController(this, '');
  @state() size = new WindowSizeController(this);

  override render() {
    return permissionGuard(ChancenPermission.campus.EDIT_SCOPE, () => this._content(), {
      forbiddenTemplate: html`<campus-page-no-permission></campus-page-no-permission>`,
      context: ANY_CONTEXT,
    });
  }

  private _content() {
    return html`
      <cr-flex padding="${this.size.mobile ? 'md' : 'lg'}" space="lg">
          <cr-h2> Scopeverwaltung </cr-h2>
          <cr-text> Von hier aus kannst du die Scopes verwalten. Alle Themen, Lektionen, Aufgaben und Events sind jeweils einem Scope zugeteilt, um mehrere Instanzen eines Campus zu ermöglichen. </cr-text>
        </cr-flex>
        <cr-flex
          ${css({
            position: 'sticky',
            top: '0',
            zIndex: '1',
            background: this.colors.neutral.shade(50),
          })}
          padding="md"
          w100
        >
          <cr-flex
            horizontal
            crossaxis-center
            padding="${this.size.mobile ? 'md' : 'lg'}" space="lg"
            ${css({
              background: this.colors.neutral.shade(50),
              paddingTop: this.spacing.md,
              paddingBottom: this.spacing.md,
            })}
          >
            <cr-input
              @value-changed="${(e: ValueEvent<string>) => {
                if (this._userSearchText.value === e.detail.value) return;
                this._userSearchText.next(e.detail.value);
              }}"
              placeholder="Suchen..."
              icon="search"
              flex
            ></cr-input>
            <cr-button icon="add" @click=${() =>
              CampusDialogEditScope.showPopup({
                draggable: this.size.mobile || this.size.tablet,
              })}>Scope</cr-button>
          </cr-flex>
        </cr-flex>
          
        </cr-flex>
        <cr-grid columns="3" padding="${this.size.mobile ? 'md' : 'lg'}" space="lg">
          <cr-text bold>Name</cr-text>
          <cr-text bold>ID</cr-text>
          <cr-text bold>Funktionen</cr-text>
          ${repeat(
            this._filterScopes(this._scopes.value ?? []),
            scope => scope._id,
            scope => this._getScopeEntry(scope),
          )}
        </cr-grid>
      </cr-flex>
    `;
  }

  private _getScopeEntry(scope: CampusScope) {
    return html`
      <cr-text>${scope.name}</cr-text>
      <cr-text>${scope._id}</cr-text>
      <cr-flex horizontal space="md">
        <cr-icon ${css({ cursor: 'pointer' })} @click=${() => alert('goto')}>arrow_forward</cr-icon>
        ${this._currentUser.value?.checkRole(Role.ADMIN)
          ? html`
              <cr-icon
                ${css({ cursor: 'pointer' })}
                @click=${() =>
                  CampusDialogEditScope.showPopup({
                    draggable: this.size.mobile || this.size.tablet,
                    props: { scope },
                  })}
              >
                edit
              </cr-icon>
            `
          : nothing}
      </cr-flex>
    `;
  }

  // TODO Build paginated filter, not done yet cause there are really few scopes
  private _filterScopes(scopes: CampusScope[]) {
    // lesson.name.toLocaleLowerCase().includes(this.userSearchText!.value.toLocaleLowerCase()),
    if (this._userSearchText.value.length) {
      return scopes.filter(s => s.name.toLocaleLowerCase().includes(this._userSearchText.value.toLocaleLowerCase()));
    }
    return scopes;
  }
}
